import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Icon } from '@iconify/react';
import Sidebar from '../../components/Sidebar';
import subscriptionManager from '../../utils/subscriptionManager';
import { ContentSpinner, useToast } from '../../components/ui';

function CreditsPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userSubscription, setUserSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const subscription = await subscriptionManager.getUserSubscription();
        setUserSubscription(subscription);
      } catch (error) {
        console.error('Error loading subscription:', error);
        setUserSubscription(subscriptionManager.getDefaultSubscription());
      } finally {
        setLoading(false);
      }
    };

    loadSubscription();
  }, []);

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      // TODO: Call API to cancel subscription
      // await apiClient.post('/subscriptions/cancel');
      toast.success('取消訂閱請求已發送，我們會寄送確認信到您的 Email');
      setShowCancelModal(false);
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error('取消訂閱失敗，請稍後再試或聯繫客服');
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-white">
        <Sidebar activePage="" />
        <div className="flex-1">
          <ContentSpinner />
        </div>
      </div>
    );
  }

  const currentCredits = 692; // 這裡之後從 API 獲取

  return (
    <div className="flex min-h-screen" style={{ background: theme.bgPrimary }}>
      <Sidebar activePage="" />

      <div className="flex-1 p-10 max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-[1.75rem] font-bold mb-2" style={{ color: theme.textPrimary }}>
            <Icon icon="mdi:lightning-bolt" className="mr-3 text-amber-500" />
            Credits
          </h1>
          <p className="text-sm" style={{ color: theme.textSecondary }}>
            管理您的積分和使用額度
          </p>
        </div>

        {/* Current Credits Card */}
        <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-2 border-amber-500 rounded-2xl p-8 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm mb-2" style={{ color: theme.textSecondary }}>
                當前可用積分
              </div>
              <div className="text-5xl font-bold text-amber-500">
                {currentCredits}
              </div>
            </div>
            <Icon icon="mdi:lightning-bolt" className="text-[96px] text-amber-500 opacity-20" />
          </div>
        </div>

        {/* Plan Info */}
        {userSubscription && (
          <div
            className="rounded-xl p-6 mb-6"
            style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
          >
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.textPrimary }}>
              當前訂閱方案
            </h2>
            <div className="grid grid-cols-3 gap-4 mb-5">
              <div>
                <div className="text-sm mb-1" style={{ color: theme.textSecondary }}>
                  方案名稱
                </div>
                <div className="text-base font-semibold" style={{ color: theme.textPrimary }}>
                  {userSubscription.accountPlans.map(p => p.name).join(' + ')}
                </div>
              </div>
              <div>
                <div className="text-sm mb-1" style={{ color: theme.textSecondary }}>
                  月費
                </div>
                <div className="text-base font-semibold" style={{ color: theme.textPrimary }}>
                  NT${userSubscription.totalPrice.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm mb-1" style={{ color: theme.textSecondary }}>
                  下次計費日期
                </div>
                <div className="text-base font-semibold" style={{ color: theme.textPrimary }}>
                  {new Date(userSubscription.nextBillingDate).toLocaleDateString('zh-TW')}
                </div>
              </div>
            </div>

            {/* Payment Method Section */}
            <div className="pt-5 mb-5" style={{ borderTop: `1px solid ${theme.borderColor}` }}>
              <h3 className="text-base font-semibold mb-3" style={{ color: theme.textPrimary }}>
                付款方式
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-8 rounded-md flex items-center justify-center"
                    style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}` }}
                  >
                    <Icon icon="mdi:credit-card-outline" className="text-xl" style={{ color: theme.textPrimary }} />
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>
                      •••• •••• •••• 4242
                    </div>
                    <div className="text-xs" style={{ color: theme.textSecondary }}>
                      到期日：12/25
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/settings?tab=payment')}
                  className="px-4 py-2 bg-transparent rounded-md text-[13px] font-medium cursor-pointer"
                  style={{ color: theme.buttonPrimary, border: `1px solid ${theme.buttonPrimary}` }}
                >
                  更換卡片
                </button>
              </div>
            </div>

            {/* Auto Renewal Status */}
            <div className="pt-5" style={{ borderTop: `1px solid ${theme.borderColor}` }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: theme.textPrimary }}>
                    自動續訂
                  </div>
                  <div className="text-xs" style={{ color: theme.textSecondary }}>
                    訂閱將於 {new Date(userSubscription.nextBillingDate).toLocaleDateString('zh-TW')} 自動續訂
                  </div>
                </div>
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="px-4 py-2 bg-transparent text-red-500 border border-red-500 rounded-md text-[13px] font-medium cursor-pointer flex items-center gap-1.5"
                >
                  <Icon icon="mdi:cancel" />
                  取消續訂
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Usage Statistics */}
        <div
          className="rounded-xl p-6 mb-6"
          style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
        >
          <h2 className="text-xl font-semibold mb-4" style={{ color: theme.textPrimary }}>
            本月使用狀況
          </h2>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <div className="text-sm mb-2" style={{ color: theme.textSecondary }}>
                已使用積分
              </div>
              <div className="text-[32px] font-bold" style={{ color: theme.textPrimary }}>
                58
              </div>
              <div className="text-xs mt-1" style={{ color: theme.textTertiary }}>
                本月已使用
              </div>
            </div>
            <div>
              <div className="text-sm mb-2" style={{ color: theme.textSecondary }}>
                生成內容
              </div>
              <div className="text-[32px] font-bold" style={{ color: theme.textPrimary }}>
                45
              </div>
              <div className="text-xs mt-1" style={{ color: theme.textTertiary }}>
                篇文章
              </div>
            </div>
            <div>
              <div className="text-sm mb-2" style={{ color: theme.textSecondary }}>
                圖片生成
              </div>
              <div className="text-[32px] font-bold" style={{ color: theme.textPrimary }}>
                23
              </div>
              <div className="text-xs mt-1" style={{ color: theme.textTertiary }}>
                張圖片
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/pricing')}
            className="px-6 py-3 border-none rounded-lg text-sm font-semibold cursor-pointer flex items-center gap-2"
            style={{ background: theme.buttonPrimary, color: theme.buttonPrimaryText }}
          >
            <Icon icon="mdi:crown" />
            升級方案
          </button>
          <button
            onClick={() => navigate('/pricing')}
            className="px-6 py-3 rounded-lg text-sm font-semibold cursor-pointer flex items-center gap-2"
            style={{ background: theme.buttonSecondary, color: theme.textPrimary, border: `1px solid ${theme.borderColor}` }}
          >
            <Icon icon="mdi:cog" />
            管理訂閱
          </button>
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999]"
          onClick={() => setShowCancelModal(false)}
        >
          <div
            className="rounded-2xl p-8 max-w-[480px] w-[90%] shadow-2xl"
            style={{ background: theme.bgSecondary }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <Icon icon="mdi:alert-circle-outline" className="text-6xl text-red-500 mb-4" />
              <h2 className="text-2xl font-bold mb-3" style={{ color: theme.textPrimary }}>
                確定要取消訂閱嗎？
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: theme.textSecondary }}>
                取消訂閱後，您將在當前計費週期結束時失去所有訂閱權益。<br/>
                我們會寄送確認信到您的 Email。
              </p>
            </div>

            <div
              className="rounded-xl p-4 mb-6"
              style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}` }}
            >
              <div className="text-[13px] mb-2" style={{ color: theme.textSecondary }}>
                您將失去的權益：
              </div>
              <ul className="text-[13px] m-0 pl-5" style={{ color: theme.textPrimary }}>
                <li>每月 {userSubscription?.accountPlans[0]?.name || ''} 點數配額</li>
                <li>AI 生成文字、圖片功能</li>
                <li>優先客服支援</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-6 py-3 rounded-lg text-sm font-semibold cursor-pointer"
                style={{ background: theme.buttonSecondary, color: theme.textPrimary, border: `1px solid ${theme.borderColor}` }}
              >
                保留訂閱
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className={`flex-1 px-6 py-3 bg-red-500 text-white border-none rounded-lg text-sm font-semibold flex items-center justify-center gap-2 ${
                  cancelLoading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                }`}
              >
                {cancelLoading ? (
                  <>
                    <Icon icon="mdi:loading" className="animate-spin text-lg" />
                    處理中...
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:cancel" />
                    確定取消
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreditsPage;
